import type { UploadFile } from 'antd';
import type { File } from '../types';

/** Entities that persist images as `images` array (e.g. Issue, Cause). */
export type ImageEntity = {
  images?: File[];
};

export function entityToUploadFileList(entity?: ImageEntity): UploadFile[] {
  const images = entity?.images ?? [];
  if (!images.length) return [];
  return images.map((img, i) => ({
    uid: `stored-${img.id}`,
    name: img.filename || `image-${i + 1}.jpg`,
    status: 'done' as const,
    url: img.url,
    thumbUrl: img.url,
    response: {
      data: [img],
    },
  }));
}

export function uploadFileListToEntityFiles(
  fileList: UploadFile[],
  fallbackEntity?: ImageEntity
): { images: File[] } {
  const prevImages = fallbackEntity?.images ?? [];

  const images: File[] = fileList
    .filter((f) => f.status === 'done')
    .flatMap((f) => {
      const row = f.response?.data?.[0] as
        | { id?: number; fileUrl?: string; url?: string; filename?: string }
        | undefined;

      const url = (row?.fileUrl || row?.url || f.url) as string | undefined;
      if (!url) return [];

      let id: number | undefined;
      let filename: string | undefined;

      if (f.uid.startsWith('stored-')) {
        const storedId = parseInt(f.uid.replace('stored-', ''), 10);
        if (!Number.isNaN(storedId)) {
          id = storedId;
        } else {
          const idx = prevImages.findIndex((pf) => pf.url === url);
          if (idx !== -1) {
            id = prevImages[idx].id;
            filename = prevImages[idx].filename;
          }
        }
      } else {
        id = typeof row?.id === 'number' ? row.id : undefined;
        filename = row?.filename ?? f.name;
        if (id === undefined) {
          const idx = prevImages.findIndex((pf) => pf.url === url);
          if (idx !== -1) {
            id = prevImages[idx].id;
            filename = prevImages[idx].filename;
          }
        }
      }

      if (!id) return [];

      return [{ id, url, filename: filename ?? f.name }];
    });

  const validImages = images.filter((img): img is File => img.id > 0);

  if (validImages.length === 0) {
    return { images: [] };
  }

  return { images: validImages };
}
